import React, { MouseEvent } from 'react';
import styled from '@emotion/styled';
import queryString from 'query-string';

// components
import GoogleLogo from 'js/common/components/GoogleLogo';

// styles
import { mq } from 'js/common/utils/mediaQueries';
import { thirdPartyColors } from 'js/auth/utils/palette';

// types
import { Search } from 'js/auth/types/search';
import { getSignUpSource } from 'js/auth/types/signup';
import { PartnerType } from 'js/common/types/billing';

// css variables
const buttonSize = 40;

const GoogleButton = styled.button(
  mq({
    display: 'flex',
    flexDirection: 'row',
    padding: '0 2px 0 0',
    border: '1px solid',
    borderRadius: 2,
    width: [296, 'calc(100vw - 36px)', 296, 296],
    boxSizing: 'content-box',
  }),
  (props: { disabled: boolean }) =>
    props.disabled
      ? {
          backgroundColor: thirdPartyColors.googleBtnBackgroundDisabled,
          borderColor: thirdPartyColors.googleBtnBackgroundDisabled,
        }
      : {
          cursor: 'pointer',
          backgroundColor: thirdPartyColors.googleBtnBackgroundBase,
          borderColor: thirdPartyColors.googleBtnBackgroundBase,
          ':hover, :focus': {
            backgroundColor: thirdPartyColors.googleBtnBackgroundDark1,
            borderColor: thirdPartyColors.googleBtnBackgroundDark1,
            svg: {
              filter: 'brightness(0.95)',
            },
          },
          ':active': {
            backgroundColor: thirdPartyColors.googleBtnBackgroundDark2,
            borderColor: thirdPartyColors.googleBtnBackgroundDark2,
            svg: {
              filter: 'brightness(0.85)',
            },
          },
        }
);

const DisplayTextContainer = styled.div({
  flexGrow: 1,
  height: buttonSize,
});

const DisplayText = styled.div({
  color: thirdPartyColors.googleBtnText,
  fontSize: 13,
  fontWeight: 'bold',
  fontFamily: 'Euclid Circular A',
  lineHeight: `${buttonSize}px`,
  height: buttonSize,
});

export interface GoogleAuthButtonProps {
  windowLocation?: Pick<Location, 'assign'>;
  location?: Pick<Location, 'search'>;
  displayText?: string;
  disabled?: boolean;
  className?: string;
  clientState?: Search;
  marketplaceLinkFlow?: PartnerType;
}

export default function GoogleAuthButton({
  windowLocation = window.location,
  location: { search } = { search: '' },
  displayText = 'Google Login',
  disabled = false,
  className,
  marketplaceLinkFlow,
  clientState = {},
}: GoogleAuthButtonProps) {
  const onClick = async (e: MouseEvent) => {
    e.preventDefault();
    const { isVercelIntegration } = clientState;
    const signupSource = getSignUpSource(marketplaceLinkFlow, isVercelIntegration);
    const queryParams = { ...(queryString.parse(search) as Search), signupSource, ...clientState };
    const googleSsoUrl = `/account/sso/google?${queryString.stringify(queryParams)}`;
    windowLocation.assign(googleSsoUrl);
  };

  return (
    <GoogleButton disabled={disabled} type="button" onClick={onClick} className={className}>
      <GoogleLogo size={buttonSize} fillColor="#FFF" />
      <DisplayTextContainer>
        <DisplayText>{displayText}</DisplayText>
      </DisplayTextContainer>
    </GoogleButton>
  );
}
